import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { AppLayout } from "@/components/AppLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { 
  FolderOpen, 
  Plus, 
  Search, 
  MoreVertical, 
  FileText, 
  Trash2, 
  Eye,
  Clock
} from "lucide-react";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Templates({ user, setUser }) {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [deleteDialog, setDeleteDialog] = useState({ open: false, template: null });

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      const response = await fetch(`${API_URL}/api/templates`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setTemplates(data);
      }
    } catch (error) {
      console.error('Error fetching templates:', error);
      toast.error("Erreur lors du chargement des modèles");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteDialog.template) return;

    try {
      const response = await fetch(`${API_URL}/api/templates/${deleteDialog.template.template_id}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        toast.success("Modèle supprimé");
        setTemplates(templates.filter(t => t.template_id !== deleteDialog.template.template_id));
      } else {
        toast.error("Erreur lors de la suppression");
      }
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }

    setDeleteDialog({ open: false, template: null });
  };

  const filteredTemplates = templates.filter(t => 
    t.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    t.description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    t.category?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  return (
    <AppLayout user={user} setUser={setUser}>
      <div className="p-8" data-testid="templates-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="font-heading text-3xl font-semibold tracking-tight mb-2">
              Modèles de contrats
            </h1>
            <p className="text-muted-foreground">
              Gérez vos modèles de documents Word
            </p>
          </div>
          <Button 
            onClick={() => navigate('/templates/upload')}
            disabled={!user?.organization_id}
            data-testid="upload-template-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Nouveau modèle
          </Button>
        </div>

        {/* Search */}
        <div className="relative mb-6">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Rechercher un modèle..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 max-w-md"
            data-testid="search-templates-input"
          />
        </div>

        {/* Templates Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="spinner w-8 h-8"></div>
          </div>
        ) : filteredTemplates.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredTemplates.map((template) => (
              <Card 
                key={template.template_id} 
                className="card-hover"
                data-testid={`template-card-${template.template_id}`}
              >
                <CardHeader className="flex flex-row items-start justify-between gap-2 pb-2">
                  <div className="flex items-start gap-3 flex-1 min-w-0">
                    <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <FolderOpen className="w-5 h-5 text-primary" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <CardTitle className="text-base leading-snug line-clamp-2 break-words" title={template.name}>{template.name}</CardTitle>
                      {template.category && (
                        <Badge variant="secondary" className="mt-1 text-xs">
                          {template.category}
                        </Badge>
                      )}
                    </div>
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-8 w-8 flex-shrink-0" data-testid={`template-menu-${template.template_id}`}>
                        <MoreVertical className="w-4 h-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => navigate(`/contracts/new/${template.template_id}`)}>
                        <FileText className="w-4 h-4 mr-2" />
                        Créer un contrat
                      </DropdownMenuItem>
                      <DropdownMenuItem 
                        onClick={() => setDeleteDialog({ open: true, template })}
                        className="text-destructive"
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        Supprimer
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </CardHeader>
                <CardContent>
                  {template.description && (
                    <p className="text-sm text-muted-foreground mb-4 line-clamp-2">
                      {template.description}
                    </p>
                  )}
                  <div className="flex items-center justify-between text-xs text-muted-foreground">
                    <div className="flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {formatDate(template.created_at)}
                    </div>
                    <div className="flex items-center gap-2">
                      <span>{template.fields?.length || 0} champs</span>
                      {template.repeatable_blocks?.length > 0 && (
                        <Badge variant="outline" className="text-xs">
                          Multi-parties
                        </Badge>
                      )}
                    </div>
                  </div>
                  <Button 
                    className="w-full mt-4"
                    onClick={() => navigate(`/contracts/new/${template.template_id}`)}
                    data-testid={`create-contract-from-${template.template_id}`}
                  >
                    Créer un contrat
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <div className="empty-state" data-testid="empty-templates">
            <div className="empty-state-icon">
              <FolderOpen className="w-full h-full" />
            </div>
            <h3 className="font-semibold mb-2">Aucun modèle</h3>
            <p className="text-muted-foreground mb-4">
              {searchQuery 
                ? "Aucun modèle ne correspond à votre recherche" 
                : "Commencez par importer un modèle Word"}
            </p>
            {!searchQuery && (
              <Button onClick={() => navigate('/templates/upload')} disabled={!user?.organization_id}>
                <Plus className="w-4 h-4 mr-2" />
                Importer un modèle
              </Button>
            )}
          </div>
        )}

        {/* Delete Confirmation Dialog */}
        <AlertDialog open={deleteDialog.open} onOpenChange={(open) => setDeleteDialog({ ...deleteDialog, open })}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Supprimer ce modèle ?</AlertDialogTitle>
              <AlertDialogDescription>
                Cette action est irréversible. Le modèle "{deleteDialog.template?.name}" sera définitivement supprimé.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Annuler</AlertDialogCancel>
              <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground">
                Supprimer
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </AppLayout>
  );
}
